import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard,
  BookOpen,
  Users,
  BarChart3,
  CreditCard,
  Menu,
  X,
  LogOut,
  Settings,
  ChevronDown,
  Gift,
  Package,
  Headphones,
  Printer,
  MessageCircle,
  Shield,
  ChevronRight,
  Circle,
  Globe,
  Music,
  Calendar,
  Upload
} from 'lucide-react';

// Define navigation based on user role
const getNavigation = (user) => {
  const isSuperAdmin = user?.is_superuser;
  const isStaff = user?.user_type === 'admin' && user?.is_staff && !user?.is_superuser;
  const isLegalAdmin = user?.legal_admin;  // Add this
  const isBusinessAdmin = user?.business_admin;  // Add this
  
  const allNavigation = [
    { 
      name: 'Dashboard', 
      href: '/', 
      icon: LayoutDashboard, 
      roles: ['superadmin', 'staff', 'legal', 'business']
    },
    
    // Books Section
    { 
      name: 'Books', 
      href: '/books', 
      icon: BookOpen, 
      roles: ['superadmin', 'staff']
    },
    
    // Music Section
    { 
      name: 'Music', 
      icon: Music,
      roles: ['superadmin', 'staff'],
      children: [
        { 
          name: 'Music Management', 
          href: '/admin/music', 
          icon: Music
        },
        { 
          name: 'Upload Music', 
          href: '/admin/upload-music', 
          icon: Upload
        }
      ]
    },
    
    // Events Section
    { 
      name: 'Events', 
      icon: Calendar,
      roles: ['superadmin', 'staff'],
      children: [
        { 
          name: 'Events Management', 
          href: '/admin/events', 
          icon: Calendar
        },
        { 
          name: 'Create Event', 
          href: '/admin/create-event', 
          icon: Calendar
        }
      ]
    },
    
    // Services
    {
      name: 'Services',
      href: '/services',
      icon: Package,
      roles: ['superadmin', 'staff']
    },

    // Legal & Business Support - New Section
    {
      name: 'Legal & Business',
      icon: Shield,
      roles: ['superadmin', 'legal', 'business'],
      children: [
        ...(isSuperAdmin || isLegalAdmin ? [
          { 
            name: 'Legal Reviews', 
            href: '/legal-business/legal', 
            icon: Shield
          }
        ] : []),
        ...(isSuperAdmin || isBusinessAdmin ? [
          { 
            name: 'Business Reviews', 
            href: '/legal-business/business', 
            icon: BarChart3
          }
        ] : []),
        ...(isSuperAdmin ? [
          { 
            name: 'All Requests', 
            href: '/legal-business/all', 
            icon: Package
          }
        ] : [])
      ]
    },
    
    // Support
    {
      name: 'Support Tickets',
      href: '/support-tickets',
      icon: MessageCircle,
      roles: ['superadmin', 'staff']
    },
    
    // News Ticker
    {
      name: 'News Ticker',
      href: '/news-ticker',
      icon: Globe,
      roles: ['superadmin', 'staff']
    },
    
    // Super Admin Only Items
    { 
      name: 'Users', 
      href: '/users', 
      icon: Users, 
      roles: ['superadmin']
    },
    { 
      name: 'Sales', 
      href: '/sales', 
      icon: BarChart3, 
      roles: ['superadmin']
    },
    { 
      name: 'Payouts', 
      href: '/enhanced-payouts', 
      icon: CreditCard, 
      roles: ['superadmin']
    },
    { 
      name: 'Vouchers', 
      href: '/vouchers', 
      icon: Gift, 
      roles: ['superadmin']
    },
    {
      name: 'ISBN Management',
      href: '/isbn-management',
      icon: Package,
      roles: ['superadmin']
    },
  ];
  
  // Filter based on user role
  if (isSuperAdmin) return allNavigation;
  
  // For staff, show staff items only
  if (isStaff) {
    return allNavigation.filter(item => 
      item.roles.includes('staff') || 
      (item.children && item.children.some(child => child.roles?.includes('staff')))
    );
  }
  
  // For legal/business admins
  const allowedRoles = [];
  if (isLegalAdmin) allowedRoles.push('legal');
  if (isBusinessAdmin) allowedRoles.push('business');
  
  return allNavigation.filter(item => {
    // Include if item has a role that matches user's roles
    if (item.roles.some(role => allowedRoles.includes(role))) {
      return true;
    }
    
    // Include if item has children and any child matches user's roles
    if (item.children) {
      const hasMatchingChild = item.children.some(child => 
        child.roles?.some(role => allowedRoles.includes(role))
      );
      if (hasMatchingChild) return true;
    }
    
    // Include staff items (they're accessible by all admins)
    return item.roles.includes('staff');
  });
};

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Derive role flags from user object
  const isSuperAdmin = user?.is_superuser;
  const isStaff = user?.user_type === 'admin' && user?.is_staff && !user?.is_superuser;
  const isLegalAdmin = user?.legal_admin;  // Add this
  const isBusinessAdmin = user?.business_admin;  // Add this

  const navigation = getNavigation(user);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleExpanded = (itemName) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemName)) {
        newSet.delete(itemName);
      } else {
        newSet.add(itemName);
      }
      return newSet;
    });
  };

  const isPathActive = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    
    // Handle query params
    const pathWithoutQuery = location.pathname;
    const hrefWithoutQuery = href.split('?')[0];
    
    // Handle services routes
    if (hrefWithoutQuery === '/services') {
      return pathWithoutQuery === '/services' || 
             location.pathname.includes('/services');
    }
    
    // Handle music routes
    if (hrefWithoutQuery === '/admin/music') {
      return pathWithoutQuery === '/admin/music' || 
             location.pathname.includes('/admin/music');
    }
    
    // Handle events routes
    if (hrefWithoutQuery === '/admin/events') {
      return pathWithoutQuery === '/admin/events' || 
             location.pathname.includes('/admin/events');
    }
    
    // Handle legal-business routes
    if (hrefWithoutQuery === '/legal-business/legal') {
      return pathWithoutQuery === '/legal-business/legal' || 
             location.pathname.includes('/legal-business/legal');
    }
    
    if (hrefWithoutQuery === '/legal-business/business') {
      return pathWithoutQuery === '/legal-business/business' || 
             location.pathname.includes('/legal-business/business');
    }
    
    if (hrefWithoutQuery === '/legal-business/all') {
      return pathWithoutQuery === '/legal-business/all' || 
             location.pathname.includes('/legal-business/all');
    }
    
    return pathWithoutQuery === hrefWithoutQuery || 
           pathWithoutQuery.startsWith(hrefWithoutQuery + '/');
  };

  const isItemActive = (item) => {
    if (item.href) {
      return isPathActive(item.href);
    }
    
    if (item.children) {
      return item.children.some(child => isPathActive(child.href));
    }
    
    return false;
  };

  const NavigationItem = ({ item, depth = 0 }) => {
    const hasChildren = item.children && item.children.length > 0;
    const isActive = isItemActive(item);
    const isExpanded = expandedItems.has(item.name);
    const Icon = item.icon;
    
    return (
      <div className="mb-1">
        {hasChildren ? (
          <>
            <button
              onClick={() => toggleExpanded(item.name)}
              className={`flex items-center justify-between w-full px-4 py-3.5 text-sm font-semibold rounded-xl transition-all duration-200 group ${
                isActive
                  ? 'bg-[#1d1d1d] text-white'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center">
                <Icon className={`mr-3.5 h-5 w-5 transition-transform duration-200 ${
                  isActive ? 'text-white' : 'text-gray-400 group-hover:text-[#1d1d1d]'
                }`} />
                {item.name}
              </div>
              <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${
                isExpanded ? 'rotate-90' : ''
              } ${isActive ? 'text-white' : 'text-gray-400'}`} />
            </button>
            
            {isExpanded && (
              <div className="ml-4 mt-1 pl-2 border-l border-gray-200 space-y-1">
                {item.children.map((child) => {
                  const ChildIcon = child.icon;
                  const isChildActive = isPathActive(child.href);
                  
                  return (
                    <Link
                      key={child.name}
                      to={child.href}
                      className={`flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                        isChildActive
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                      }`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <ChildIcon className="h-4 w-4 mr-3 text-gray-400" />
                      {child.name}
                    </Link>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <Link
            to={item.href}
            className={`flex items-center px-4 py-3.5 text-sm font-semibold rounded-xl transition-all duration-200 group ${
              isActive
                ? 'bg-[#1d1d1d] text-white shadow-lg shadow-black/10'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
            onClick={() => setSidebarOpen(false)}
          >
            <Icon className={`mr-3.5 h-5 w-5 transition-transform duration-200 ${
              isActive ? 'text-white' : 'text-gray-400 group-hover:text-[#1d1d1d]'
            }`} />
            {item.name}
          </Link>
        )}
      </div>
    );
  };

  // Get user role display text
  const getUserRoleText = () => {
    if (isSuperAdmin) return 'Super Administrator';
    if (isLegalAdmin) return 'Legal Administrator';
    if (isBusinessAdmin) return 'Business Administrator';
    if (isStaff) return 'Staff Member';
    return 'User';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 flex flex-col w-80 bg-white shadow-2xl transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static lg:inset-0 transition-all duration-300 ease-in-out border-r border-gray-100`}>
        {/* Logo Section */}
        <div className="flex items-center justify-between h-20 px-6 border-b border-gray-100 bg-white flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <img 
                src="/Suavemente_logo.png" 
                alt="Suavemente Publishing" 
                className="h-16 w-auto"
              />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Silk SPACE</h1>
              <div className="flex items-center space-x-1 mt-1">
                <Shield className={`h-3 w-3 ${
                  isSuperAdmin ? 'text-red-500' : 
                  isLegalAdmin ? 'text-purple-500' :
                  isBusinessAdmin ? 'text-blue-500' : 'text-gray-500'
                }`} />
                <span className="text-xs font-medium text-gray-500">
                  {getUserRoleText()}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-all duration-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <div className="flex-1 flex flex-col overflow-y-auto py-6">
          <nav className="px-4 space-y-1 flex-1">
            {navigation.map((item) => (
              <NavigationItem key={item.name} item={item} />
            ))}
          </nav>

          {/* User Section */}
          <div className="flex-shrink-0 border-t border-gray-100 p-6">
            <div className="flex items-center space-x-4 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors duration-200 cursor-pointer">
              <div className="flex-shrink-0">
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center shadow-md ${
                  isSuperAdmin ? 'bg-gradient-to-br from-red-500 to-red-600' :
                  isLegalAdmin ? 'bg-gradient-to-br from-purple-500 to-purple-600' :
                  isBusinessAdmin ? 'bg-gradient-to-br from-blue-500 to-cyan-600' :
                  'bg-gradient-to-br from-gray-500 to-gray-600'
                }`}>
                  <span className="text-white text-sm font-bold">
                    {user?.username?.charAt(0).toUpperCase() || 'A'}
                  </span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {user?.first_name && user?.last_name 
                    ? `${user.first_name} ${user.last_name}`
                    : user?.username || 'User'
                  }
                </p>
                <p className="text-xs text-gray-500 truncate font-medium">
                  {user?.email || 'user@suavemente.com'}
                </p>
                <div className="flex items-center mt-1">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    isSuperAdmin ? 'bg-red-100 text-red-800' :
                    isLegalAdmin ? 'bg-purple-100 text-purple-800' :
                    isBusinessAdmin ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {getUserRoleText()}
                  </span>
                  {isStaff && !isSuperAdmin && !isLegalAdmin && !isBusinessAdmin && (
                    <span className="text-xs text-gray-500 ml-2">Limited Access</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Navigation Bar */}
        <header className="flex-shrink-0 bg-white/80 backdrop-blur-xl shadow-sm border-b border-gray-100">
          <div className="flex items-center justify-between h-20 px-8">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2.5 rounded-xl text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-all duration-200 mr-3"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="flex items-center space-x-3 text-gray-600">
                <div className={`p-2 rounded-lg ${
                  isSuperAdmin ? 'bg-red-100' :
                  isLegalAdmin ? 'bg-purple-100' :
                  isBusinessAdmin ? 'bg-blue-100' :
                  'bg-gray-100'
                }`}>
                  <Settings className={`h-4 w-4 ${
                    isSuperAdmin ? 'text-red-600' :
                    isLegalAdmin ? 'text-purple-600' :
                    isBusinessAdmin ? 'text-blue-600' :
                    'text-gray-600'
                  }`} />
                </div>
                <div>
                  <p className="text-sm font-semibold">Administration Panel</p>
                  <p className="text-xs text-gray-500">
                    Welcome back, {user?.first_name || 'Admin'} • {
                      isSuperAdmin ? 'Full Access' :
                      isLegalAdmin ? 'Legal Access' :
                      isBusinessAdmin ? 'Business Access' :
                      'Limited Access'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* User Dropdown */}
            <div className="relative">
              <button
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                className="flex items-center space-x-4 p-2 rounded-xl hover:bg-gray-50 transition-all duration-200 group"
              >
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-semibold text-gray-900">
                    {user?.first_name || user?.username || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 font-medium">
                    {getUserRoleText()}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center shadow-md ${
                    isSuperAdmin ? 'bg-gradient-to-br from-red-500 to-red-600' :
                    isLegalAdmin ? 'bg-gradient-to-br from-purple-500 to-purple-600' :
                    isBusinessAdmin ? 'bg-gradient-to-br from-blue-500 to-cyan-600' :
                    'bg-gradient-to-br from-gray-500 to-gray-600'
                  }`}>
                    <span className="text-white text-sm font-bold">
                      {user?.username?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${userDropdownOpen ? 'rotate-180' : ''}`} />
                </div>
              </button>

              {/* Dropdown Menu */}
              {userDropdownOpen && (
                <div className="absolute right-0 mt-3 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50 backdrop-blur-xl">
                  <div className="px-5 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {user?.first_name && user?.last_name 
                        ? `${user.first_name} ${user.last_name}`
                        : user?.username || 'User'
                      }
                    </p>
                    <p className="text-xs text-gray-500 truncate font-medium mt-1">
                      {user?.email}
                    </p>
                    <div className="flex items-center mt-2">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        isSuperAdmin ? 'bg-red-100 text-red-800' :
                        isLegalAdmin ? 'bg-purple-100 text-purple-800' :
                        isBusinessAdmin ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {getUserRoleText()}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-5 py-3 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors duration-200 font-medium"
                  >
                    <LogOut className="h-4 w-4 mr-3" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          <div className="p-8">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden transition-all duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}